import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandUpdate, ThemePatch } from '@tallyframe/core';
import { api } from './client';
import { queryKeys } from './queries';

// `enabled` lets a screen gate the request (for example on an entitlement) without a conditional hook.
export function useBrandQuery(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.brand(workspaceId),
    queryFn: ({ signal }) => api.getBrand(signal),
    enabled
  });
}

export function useUpdateBrand(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: BrandUpdate) => api.updateBrand(update),
    onSuccess: (brand) => client.setQueryData(queryKeys.brand(workspaceId), brand)
  });
}

export function useThemeQuery(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.theme(workspaceId),
    queryFn: ({ signal }) => api.getTheme(signal),
    enabled
  });
}

// The cached ThemeView also carries `applied` from ThemeUpdateResult; screens read `applied` from the mutation result.
export function useUpdateTheme(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: ThemePatch) => api.updateTheme(patch),
    onSuccess: (result) => client.setQueryData(queryKeys.theme(workspaceId), result)
  });
}

// Upload and delete answer with the new brand, so the brand entry is written, not refetched.
export function useUploadLogo(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadLogo(file),
    onSuccess: (brand) => client.setQueryData(queryKeys.brand(workspaceId), brand)
  });
}

export function useDeleteLogo(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteLogo(),
    onSuccess: (brand) => client.setQueryData(queryKeys.brand(workspaceId), brand)
  });
}

export function useApiTokens(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.tokens(workspaceId),
    queryFn: () => api.listTokens().then((r) => r.tokens),
    enabled
  });
}

export function useCreateToken(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createToken(name),
    gcTime: 0, // the result carries the plaintext token; do not keep it in the mutation cache
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.tokens(workspaceId) })
  });
}

export function useRevokeToken(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => api.revokeToken(tokenId),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.tokens(workspaceId) })
  });
}

export function useRenameWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; name: string }) => api.renameWorkspace(v.id, v.name),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.workspaces() })
  });
}

export function useDeleteWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; confirmName: string }) => api.deleteWorkspace(v.id, v.confirmName),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.workspaces() })
  });
}

export function useDeleteAccount() {
  return useMutation({ mutationFn: (confirmEmail: string) => api.deleteAccount(confirmEmail) });
}
